import {
  buildJianyingSlotSections,
  compareJianyingSlotsByTimeline,
} from '../../../../../automation/jianying/utils/jianyingSlots.js';

const normalizeArray = (value) => (
  [...new Set((Array.isArray(value) ? value : []).map(String).filter(Boolean))]
);

const getGlobalTimelineStart = (slot) => {
  const start = Number(slot?.slot_settings?.timeline_start_us);
  return Number.isFinite(start) ? start : null;
};

const getGlobalTimelineInterval = (slot) => {
  const start = getGlobalTimelineStart(slot);
  const duration = Number(slot?.slot_settings?.timeline_duration_us);
  return start !== null && Number.isFinite(duration) && duration > 0
    ? { start, end: start + duration }
    : null;
};

const isBindingTarget = (slot) => slot?.kind === 'text' || slot?.kind === 'audio';
const isReplaceableSlot = (slot) => slot?.replace_enabled !== false;

const getSlotSearchValue = (slot) => ([
  slot?.name,
  slot?.original_content,
  slot?.id,
  slot?.material_id,
  slot?.segment_id,
  slot?.occurrence_id,
].filter(Boolean).join(' ').toLocaleLowerCase());

export const getJianyingBindingImageSlots = (template) => (
  (Array.isArray(template?.slots) ? template.slots : [])
    .filter((slot) => slot?.id && slot.kind === 'image' && isReplaceableSlot(slot))
    .slice()
    .sort(compareJianyingSlotsByTimeline)
);

const buildBindingSections = (template, acceptsSlot, search = '') => {
  const normalizedSearch = String(search || '').trim().toLocaleLowerCase();
  const filterNode = (node, ancestorMatches = false) => {
    if (node.type === 'slot') {
      return acceptsSlot(node.slot)
        && (ancestorMatches
          || !normalizedSearch
          || getSlotSearchValue(node.slot).includes(normalizedSearch))
        ? node
        : null;
    }
    const groupMatches = ancestorMatches
      || !normalizedSearch
      || String(node.group.name || '').toLocaleLowerCase().includes(normalizedSearch);
    const children = node.children.flatMap((child) => {
      const filtered = filterNode(child, groupMatches);
      return filtered ? [filtered] : [];
    });
    return children.length ? { ...node, children } : null;
  };

  const sections = [];
  const visibleUngrouped = [];
  for (const node of buildJianyingSlotSections(template)) {
    const filtered = filterNode(node);
    if (!filtered) continue;
    if (filtered.type === 'group') sections.push(filtered);
    else visibleUngrouped.push(filtered);
  }
  if (visibleUngrouped.length) {
    sections.push({
      type: 'ungrouped',
      key: 'ungrouped',
      children: visibleUngrouped,
    });
  }
  return sections;
};

export const buildBindingImageSections = (template) => (
  buildBindingSections(template, (slot) => slot.kind === 'image')
);

export const buildBindingTargetSections = (template, search = '') => (
  buildBindingSections(template, isBindingTarget, search)
);

export const buildBindingOwnerMap = (imageSlots) => {
  const owners = new Map();
  for (const image of Array.isArray(imageSlots) ? imageSlots : []) {
    for (const targetId of [
      ...normalizeArray(image.text_slot_ids),
      ...normalizeArray(image.audio_slot_ids),
    ]) {
      owners.set(targetId, image);
    }
  }
  return owners;
};

export const buildBindingSlotPathMap = (template) => {
  const groups = Array.isArray(template?.slot_groups) ? template.slot_groups : [];
  const groupsById = new Map(groups.map((group) => [String(group.id), group]));
  return new Map((Array.isArray(template?.slots) ? template.slots : []).map((slot) => {
    const groupNames = [];
    let groupId = slot?.group_id ? String(slot.group_id) : null;
    for (let depth = 0; groupId && depth <= groups.length; depth += 1) {
      const group = groupsById.get(groupId);
      if (!group) break;
      groupNames.unshift(group.name || '未命名组');
      groupId = group.parent_group_id ? String(group.parent_group_id) : null;
    }
    return [
      String(slot.id),
      [...(groupNames.length ? groupNames : ['未分组']), slot.name || '未命名槽位'].join(' / '),
    ];
  }));
};

export const getBoundSlotsForImage = (template, imageSlot) => {
  const boundIds = new Set([
    ...normalizeArray(imageSlot?.text_slot_ids),
    ...normalizeArray(imageSlot?.audio_slot_ids),
  ]);
  return (Array.isArray(template?.slots) ? template.slots : [])
    .filter((slot) => (
      slot?.id
      && isReplaceableSlot(slot)
      && isBindingTarget(slot)
      && boundIds.has(String(slot.id))
    ))
    .slice()
    .sort(compareJianyingSlotsByTimeline);
};

export const setBindingGroupOpen = (collapsedGroupIds, groupId, open) => {
  const next = new Set(collapsedGroupIds);
  if (open) next.delete(groupId); else next.add(groupId);
  return next;
};

export const getBindingDraft = (imageSlot, template = null) => {
  const allowedByKind = template ? new Map([
    ['text', new Set((template.slots || [])
      .filter((slot) => slot?.kind === 'text' && isReplaceableSlot(slot))
      .map((slot) => String(slot.id)))],
    ['audio', new Set((template.slots || [])
      .filter((slot) => slot?.kind === 'audio' && isReplaceableSlot(slot))
      .map((slot) => String(slot.id)))],
  ]) : null;
  return {
    text_slot_ids: normalizeArray(imageSlot?.text_slot_ids)
      .filter((id) => !allowedByKind || allowedByKind.get('text').has(id)),
    audio_slot_ids: normalizeArray(imageSlot?.audio_slot_ids)
      .filter((id) => !allowedByKind || allowedByKind.get('audio').has(id)),
  };
};

export const updateBindingDraft = (template, imageSlotId, draft) => (
  replaceBindingImageInTemplate(template, {
    image_slot_id: imageSlotId,
    ...getBindingDraft(draft, template),
  })
);

export const applyQuickBindings = (template) => {
  const imageSlots = getJianyingBindingImageSlots(template);
  const imageIntervals = imageSlots.flatMap((image) => {
    const interval = getGlobalTimelineInterval(image);
    return interval ? [{ image, ...interval }] : [];
  });
  const targets = (Array.isArray(template?.slots) ? template.slots : [])
    .filter((slot) => slot?.id && isReplaceableSlot(slot) && isBindingTarget(slot))
    .slice()
    .sort(compareJianyingSlotsByTimeline);
  const drafts = new Map(imageSlots.map((image) => [String(image.id), {
    text_slot_ids: [],
    audio_slot_ids: [],
  }]));

  for (const target of targets) {
    const targetStart = getGlobalTimelineStart(target);
    if (targetStart === null) continue;
    let owner = null;
    let ownerStart = null;
    for (const interval of imageIntervals) {
      if (interval.start <= targetStart && targetStart < interval.end
        && (ownerStart === null || interval.start > ownerStart)) {
        owner = interval.image;
        ownerStart = interval.start;
      }
    }
    if (!owner) continue;
    const field = target.kind === 'text' ? 'text_slot_ids' : 'audio_slot_ids';
    drafts.get(String(owner.id))[field].push(String(target.id));
  }

  return imageSlots.reduce(
    (current, image) => updateBindingDraft(current, image.id, drafts.get(String(image.id))),
    template,
  );
};

export const buildBindingBatchPayload = (template) => ({
  jianying_template_id: template?.id,
  bindings: getJianyingBindingImageSlots(template).map((image) => ({
    image_slot_id: image.id,
    ...getBindingDraft(image, template),
  })),
});

export const isBindingTemplateDirty = (baselineTemplate, draftTemplate) => {
  const baselineImages = new Map(
    getJianyingBindingImageSlots(baselineTemplate)
      .map((image) => [String(image.id), getBindingDraft(image, baselineTemplate)]),
  );
  const draftImages = getJianyingBindingImageSlots(draftTemplate);
  return draftImages.length !== baselineImages.size || draftImages.some((image) => {
    const baseline = baselineImages.get(String(image.id));
    return !baseline || isBindingDraftDirty(baseline, getBindingDraft(image, draftTemplate));
  });
};

export const toggleBindingTarget = (draft, slot) => {
  const field = slot?.kind === 'text'
    ? 'text_slot_ids'
    : slot?.kind === 'audio'
      ? 'audio_slot_ids'
      : null;
  if (!field || !slot?.id) return draft;
  const ids = normalizeArray(draft?.[field]);
  return {
    ...getBindingDraft(draft),
    [field]: ids.includes(String(slot.id))
      ? ids.filter((id) => id !== String(slot.id))
      : [...ids, String(slot.id)],
  };
};

const sameIds = (left, right) => {
  const leftIds = normalizeArray(left);
  const rightIds = new Set(normalizeArray(right));
  return leftIds.length === rightIds.size && leftIds.every((id) => rightIds.has(id));
};

export const isBindingDraftDirty = (baseline, draft) => (
  !sameIds(baseline?.text_slot_ids, draft?.text_slot_ids)
  || !sameIds(baseline?.audio_slot_ids, draft?.audio_slot_ids)
);

const replaceBindingImageInTemplate = (template, savedBinding) => {
  const imageSlotId = savedBinding?.image_slot_id;
  const bindingDraft = getBindingDraft(savedBinding);
  return {
    ...template,
    slots: (Array.isArray(template?.slots) ? template.slots : []).map((slot) => (
      String(slot.id) === String(imageSlotId)
        ? { ...slot, ...bindingDraft }
        : slot
    )),
  };
};

export const getTemplateTabFocusIndex = (currentIndex, count, key) => {
  if (!count) return null;
  if (key === 'ArrowRight') return (currentIndex + 1) % count;
  if (key === 'ArrowLeft') return (currentIndex - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return null;
};
